import React, { useState, useEffect, useRef } from 'react';
import { FaFolderPlus, FaEdit, FaTrash } from 'react-icons/fa';
import './Drawer.css';
import ConfirmTooltip from '../components/Common/ConfirmTooltip';
import { formatPhoneNumber, formatSinNumber } from '../utils/formatters';

const assignedToOptions = (process.env.REACT_APP_USER_EMAILS || '').split(',').map(e => e.trim()).filter(Boolean);
const statusOptions = ['Pending', 'Draft Sent', 'Processing', 'eSigned', 'On Hold', 'Completed', 'Cancelled'];

const FORM_SECTIONS = [
  { number: 1, title: 'Client Info' },
  { number: 2, title: 'Filing Details' },
  { number: 3, title: 'Notes' },
];

const initialEntryState = {
  name: '',
  mobile: '',
  sinNumber: '',
  email: '',
  status: 'Pending',
  assignedTo: '',
  coin: '',
  receivedDate: '',
  filledDate: '',
  yearsOfFiling: [],
  notes: ''
};

const CvitpDrawer = ({ isOpen, onClose, entry, onSave, onDelete, saving, isAdmin, isDialerOpen }) => {

  const [formData, setFormData] = useState(initialEntryState);
  const [activeSection, setActiveSection] = useState(1);
  const sectionRefs = useRef({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const drawerBodyRef = useRef(null);

  const last10Years = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() - i).toString());

  useEffect(() => {
    if (!isOpen) return;
    const root = drawerBodyRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(Number(entry.target.dataset.section));
          }
        });
      },
      { root, threshold: 0.1, rootMargin: '0px 0px -75% 0px' }
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && entry && Object.keys(entry).length > 0) {
      setFormData({
        name: entry.name || '',
        mobile: formatPhoneNumber(entry.mobile),
        sinNumber: formatSinNumber(entry.sinNumber),
        email: entry.email || '',
        status: entry.status || 'Pending',
        assignedTo: entry.assignedTo || '',
        coin: entry.coin || '',
        receivedDate: entry.receivedDate || '',
        filledDate: entry.filledDate || '',
        yearsOfFiling: Array.isArray(entry.yearsOfFiling) ? entry.yearsOfFiling : (entry.yearsOfFiling ? entry.yearsOfFiling.split(',').map(s => s.trim()) : []),
        notes: entry.notes || ''
      });
    } else {
      setFormData(initialEntryState);
    }
  }, [isOpen, entry]);

  const scrollToSection = (number) => {
    sectionRefs.current[number]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === 'mobile') {
      formattedValue = formatPhoneNumber(value);
    } else if (name === 'sinNumber' || name === 'sin') {
      formattedValue = formatSinNumber(value);
    }
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleMultiSelectChange = (e) => {
    const { name } = e.target;
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, [name]: selected }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const isEditing = entry && Object.keys(entry).length > 0;

  return (
    <>
      <div className={`drawer-backdrop ${isOpen ? 'show' : ''}`}></div>
      <div className={`drawer-container ${isOpen ? 'show' : ''} ${isOpen && isDialerOpen ? 'shifted-for-dialer' : ''}`}>
        <div className="drawer-header">
          <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
            <div className="rounded-4 d-flex align-items-center justify-content-center bg-primary-subtle text-primary flex-shrink-0" style={{ width: '44px', height: '44px' }}>
              {isEditing ? <FaEdit size={18} /> : <FaFolderPlus size={18} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <h4 className="mb-0 fw-bold text-dark text-truncate">{isEditing ? 'Edit CVITP Record' : 'Add New CVITP Record'}</h4>
              <small className="text-muted text-truncate d-block">{isEditing ? `Updating record for ${entry?.name || ''}` : 'Create a new CVITP client'}</small>
            </div>
          </div>
          <button type="button" className="btn-close ms-2 flex-shrink-0" onClick={onClose} aria-label="Close"></button>
        </div>

        <div className="ledger-tab-bar">
          {FORM_SECTIONS.map(s => (
            <button key={s.number} type="button" className={`ledger-tab-pill ${activeSection === s.number ? 'active' : ''}`} onClick={() => scrollToSection(s.number)}>
              <span>{s.number}</span>
              <span>{s.title}</span>
            </button>
          ))}
        </div>

        <form id="cvitp-form" onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1" style={{ overflow: 'hidden' }}>
          <div ref={drawerBodyRef} className="drawer-body">
            <div className="row g-3">
              <h4 ref={el => sectionRefs.current[1] = el} data-section={1} className="form-section-title"><span className="section-number">1</span> Client Info</h4>
              <div className="col-md-6"><label>Full Name</label><input type="text" name="name" value={formData.name} onChange={handleChange} className="form-control" required /></div>
              <div className="col-md-6"><label>Phone Number</label><input type="tel" name="mobile" placeholder="647-855-6177" value={formData.mobile} onChange={handleChange} className="form-control" required /></div>
              <div className="col-md-6"><label>SIN Number</label><input type="text" name="sinNumber" placeholder="569 435 228" value={formData.sinNumber} onChange={handleChange} className="form-control" /></div>
              <div className="col-md-6"><label>Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control" /></div>

              <h4 ref={el => sectionRefs.current[2] = el} data-section={2} className="form-section-title"><span className="section-number">2</span> Filing Details</h4>
              <div className="col-md-6"><label>Status</label><select name="status" value={formData.status} onChange={handleChange} className="form-select">{statusOptions.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div className="col-md-6"><label>Assigned To</label><select name="assignedTo" value={formData.assignedTo} onChange={handleChange} className="form-select"><option value="">Unassigned</option>{assignedToOptions.map(e => <option key={e} value={e}>{e}</option>)}</select></div>
              <div className="col-md-6"><label>Received Date</label><input type="date" name="receivedDate" value={formData.receivedDate} onChange={handleChange} className="form-control" /></div>
              <div className="col-md-6"><label>Filed Date</label><input type="date" name="filledDate" value={formData.filledDate} onChange={handleChange} className="form-control" /></div>
              <div className="col-md-6"><label>Years of Filing</label><select multiple name="yearsOfFiling" value={formData.yearsOfFiling} onChange={handleMultiSelectChange} className="form-select" size="3">{last10Years.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
              <div className="col-md-6"><label>Coin ID</label><input type="text" name="coin" value={formData.coin} onChange={handleChange} className="form-control" /></div>

              <h4 ref={el => sectionRefs.current[3] = el} data-section={3} className="form-section-title"><span className="section-number">3</span> Notes</h4>
              <div className="col-12"><label>Internal Notes</label><textarea name="notes" value={formData.notes} onChange={handleChange} className="form-control" rows="4"></textarea></div>
            </div>
          </div>
          <div className="drawer-footer">
            {isEditing && isAdmin && (
              <div className="position-relative me-auto">
                <button type="button" className="btn btn-outline-danger rounded-pill px-4" onClick={handleDelete} disabled={saving}>
                  <FaTrash className="me-2" /> Delete
                </button>
                <ConfirmTooltip 
                  show={showDeleteConfirm} 
                  onConfirm={() => { onDelete(entry); setShowDeleteConfirm(false); }}
                  onCancel={() => setShowDeleteConfirm(false)}
                >
                  Are you sure you want to permanently delete this record? This action cannot be undone.
                </ConfirmTooltip>
              </div>
            )}
            <button type="button" className="btn btn-light border rounded-pill px-4" onClick={onClose}>Cancel</button>
            <button type="submit" form="cvitp-form" className="btn btn-primary rounded-pill px-4" disabled={saving}>
              {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Record')}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CvitpDrawer;